import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { jobRoles } from '../data/jobRoles';
import JobDescriptionModal from '../components/JobDescriptionModal';
import NeuralNetwork from '../components/NeuralNetwork';

function Careers() {
  const openRoles = jobRoles
    .filter(role => role.isOpen)
    .sort((a, b) => {
      const aIsInternship = a.employmentType === 'internship';
      const bIsInternship = b.employmentType === 'internship';
      if (aIsInternship === bIsInternship) return 0;
      return aIsInternship ? 1 : -1;
    });
  const [selectedJob, setSelectedJob] = useState(null);

  const openJobModal = (job) => {
    setSelectedJob(job);
  };

  const closeJobModal = () => {
    setSelectedJob(null);
  };

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Careers at Clariti AI | Join Our Team</title>
        <meta name="description" content="Join Clariti AI and help build the future of bank pricing. We're a small team solving big problems for banks." />
        <link rel="canonical" href="https://www.clariti.ai/careers" />
        <meta property="og:title" content="Careers at Clariti AI | Join Our Team" />
        <meta property="og:description" content="Explore open roles and help build pricing optimisation software for banks worldwide." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.clariti.ai/careers" />
        <meta name="twitter:title" content="Careers at Clariti AI | Join Our Team" />
        <meta name="twitter:description" content="Join a team building data-driven pricing software for global banking markets." />
      </Helmet>
      {/* Hero */}
      <section className="careers-hero">
        <NeuralNetwork className="nn-layer" nodeCount={40} interactive={true} />
        <div className="container">
          <h1>Join Clariti AI</h1>
          <p>We're building the future of bank pricing. Come help us.</p>
        </div>
      </section>

      {/* Dark Band */}
      <section className="section-dark-band">
        <div className="container">
          <h2>Small team, big impact</h2>
          <p>Work alongside banking experts and data scientists solving real pricing challenges.</p>
        </div>
      </section>

      {/* Why Work Here */}
      <section className="why-work-section" style={{ position: 'relative' }}>
        <NeuralNetwork className="nn-layer" nodeCount={20} interactive={false} />
        <div className="container">
          <div className="why-work-content">
            <h2>Why Clariti AI?</h2>
            <p>
              We're a small team solving big problems for banks. You'll work directly with 
              customers, ship real improvements, and see the impact of your work. No bureaucracy, 
              no fluff — just smart people building something that matters.
            </p>
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="roles-section" style={{ position: 'relative' }}>
        <NeuralNetwork className="nn-layer" nodeCount={18} interactive={false} />
        <div className="container">
          <div className="roles-content">
            <h2>Open roles</h2>
            {openRoles.length > 0 ? (
              <>
                <div className="roles-list">
                  {openRoles.map((role) => (
                    <div key={role.id} className="job-card">
                      <div className="job-card-header">
                        <div className="job-card-title-group">
                          <h3>{role.title}</h3>
                          <div className="job-card-meta">
                            <span className="job-department">{role.department}</span>
                            <div className="job-details">
                              {(() => {
                                // Split location by em dash or regular dash to separate base location from details
                                const locationParts = role.location.split(/[–—]/).map(part => part.trim());
                                const baseLocation = locationParts[0] || role.location;
                                const locationDetails = locationParts[1] || null;
                                
                                return (
                                  <>
                                    <span className="job-location">{baseLocation}</span>
                                    {locationDetails && (
                                      <>
                                        <span className="job-separator">•</span>
                                        <span className="job-location-details">{locationDetails}</span>
                                      </>
                                    )}
                                    {role.employmentType && (
                                      <>
                                        <span className="job-separator">•</span>
                                        <span className="job-employment">
                                          {role.employmentType === 'full-time' && 'Full time'}
                                          {role.employmentType === 'part-time' && `Part time${role.employmentDetails ? ` (${role.employmentDetails})` : ''}`}
                                          {role.employmentType === 'contractor' && 'Contractor'}
                                          {role.employmentType === 'internship' && `Internship${role.employmentDetails ? ` (${role.employmentDetails})` : ''}`}
                                        </span>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn btn-secondary"
                          onClick={() => openJobModal(role)}
                        >
                          View details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="no-roles-box" style={{ marginTop: '40px' }}>
                  <p>
                    <strong>Don't see a role for you?</strong><br />
                    We're always interested in hearing from talented people. If you think you'd be a great addition to the team, we'd still love to hear from you.
                  </p>
                  <p className="mt-4">
                    Email us at <a href="mailto:jobs@clariti.ai">jobs@clariti.ai</a>
                  </p>
                </div>
              </>
            ) : (
              <div className="no-roles-box">
                <p>
                  We don't have any open positions right now, but we're always interested 
                  in hearing from talented people.
                </p>
                <p className="mb-0">
                  <strong>Don't see a fit?</strong><br />
                  If you think you'd be a great addition to the team, we'd still love to hear from you.
                </p>
                <p className="mt-4">
                  Email us at <a href="mailto:jobs@clariti.ai">jobs@clariti.ai</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <JobDescriptionModal
        isOpen={selectedJob !== null}
        onClose={closeJobModal}
        job={selectedJob}
      />
    </div>
  );
}

export default Careers;
